import { API_URL } from "src/util/environment";

const createRoute = (controller: string, endpoint: string): string => `${controller}/${endpoint}`;

const CONTROLLERS = {
  CRM_CLIENT: "/crm-client/api/v1",
  CRM_CORE: "/crm-core/api/v1",
  COMMAND_SERVICE: "/command-service/api/v1",
  AUDIT_LOG: "/v1",
  WAREHOUSE: "/warehouse/api/v1",
  API: "/api/v1",
  ILA: "/ila/api/v1",
  TACHO: "/tacho/tacho/api/v1",
} as const;

const withBaseUrl = (url: string): string => `${API_URL()}${url}`;

export const ServicesUrlEndpoints = {
  AUTH: {
    LOGIN: createRoute(CONTROLLERS.CRM_CLIENT, "auth/crm-login"),
    LOGOUT: createRoute(CONTROLLERS.CRM_CLIENT, "auth/logout"),
    REFRESH: withBaseUrl(createRoute(CONTROLLERS.CRM_CLIENT, "auth/refresh")),
    SEND_OTP: createRoute(CONTROLLERS.CRM_CLIENT, "auth/initiate-2fa"),
    SESSION_TOKEN: createRoute(CONTROLLERS.CRM_CLIENT, "auth/session"),
    RESET_PASSWORD: createRoute(CONTROLLERS.CRM_CLIENT, "auth/reset-password"),
    FORGOT_PASSWORD_GENERATE: createRoute(
      CONTROLLERS.CRM_CLIENT,
      "auth/forgot-password/generate-token"
    ),
    FORGOT_PASSWORD_RESET: createRoute(CONTROLLERS.CRM_CLIENT, "auth/forgot-password"),
    VERIFY_TOKEN: createRoute(CONTROLLERS.CRM_CLIENT, "auth/forgot-password/verify-token"),
  },
  EXAMPLE: {
    LIST: createRoute(CONTROLLERS.API, "example"),
    LOOKUP: createRoute(CONTROLLERS.API, "example/lookup"),
    CREATE: createRoute(CONTROLLERS.API, "example"),
    UPDATE: createRoute(CONTROLLERS.API, "example/:id"),
    DELETE: createRoute(CONTROLLERS.API, "example/:id"),
    BULK_DELETE: createRoute(CONTROLLERS.API, "example/bulk"),
  },
  USER: {
    PROFILE: createRoute(CONTROLLERS.API, "user/profile"),
    UPDATE_PROFILE: createRoute(CONTROLLERS.API, "user/profile"),
    CHANGE_PASSWORD: createRoute(CONTROLLERS.API, "user/change-password"),
    LISTING: createRoute(CONTROLLERS.CRM_CLIENT, "user"),
    GET: createRoute(CONTROLLERS.CRM_CLIENT, "user/{userId}"),
    CREATE: createRoute(CONTROLLERS.CRM_CLIENT, "admin/user/create-user"),
    UPDATE_STATUS: createRoute(CONTROLLERS.CRM_CLIENT, "fdmr/user/{userId}/state"),
    UPDATE: createRoute(CONTROLLERS.CRM_CLIENT, "admin/user/update-user/{userId}"),
    DELETE: createRoute(CONTROLLERS.CRM_CLIENT, "user/{userId}"),
    RESEND_EMAIL: createRoute(CONTROLLERS.CRM_CLIENT, "fdmr/user/activation/reset-email"),
    ROLE_LOOKUP: createRoute(CONTROLLERS.CRM_CORE, "user-role/lookup"),
    CLIENT_LANGUAGE: createRoute(CONTROLLERS.CRM_CLIENT, "client/{clientId}/language"),
    DEVICES: createRoute(CONTROLLERS.CRM_CLIENT, "admin/devices"),
    SYSTEMS: createRoute(CONTROLLERS.CRM_CLIENT, "admin/user-systems"),
    SAVE_VEHICLES: createRoute(CONTROLLERS.CRM_CLIENT, "admin/user/{userId}/vehicles"),
    SAVE_ALLOWED_SYSTEM: createRoute(CONTROLLERS.CRM_CLIENT, "admin/user/{userId}/allowed-systems"),
    ACTIVITIES: createRoute(
      CONTROLLERS.CRM_CLIENT,
      "user-permissions/{userId}/system/{systemId}/activities"
    ),
    SAVE_ACTIVITIES: createRoute(CONTROLLERS.CRM_CLIENT, "user-permissions"),
    TIMED_DATA_PERMISSION: createRoute(
      CONTROLLERS.CRM_CLIENT,
      "user-permissions/timed-data-permission"
    ),
    TIMED_DATA_PERMISSION_BY_USER: createRoute(
      CONTROLLERS.CRM_CLIENT,
      "user-permissions/{userId}/timed-permission"
    ),
  },
  USER_LEVEL_ACCESS: {
    LOOKUP: {
      DISTRIBUTOR_LIST: createRoute(CONTROLLERS.CRM_CORE, "distributor"),
    },
  },
  DISTRIBUTOR: {
    LIMITATIONS: createRoute(CONTROLLERS.CRM_CLIENT, "distributors/{distributorId}/limits"),
  },
  LOOKUP: {
    CLIENT: createRoute(CONTROLLERS.CRM_CLIENT, "client/lookup"),
    CLIENT_PRESET: createRoute(CONTROLLERS.CRM_CLIENT, "preset/lookup"),
    CLIENT_TYPE: createRoute(CONTROLLERS.CRM_CORE, "client-type/lookup"),
    CLIENT_STATUS: createRoute(CONTROLLERS.CRM_CORE, "client-status/lookup"),
    LANGUAGE: createRoute(CONTROLLERS.CRM_CORE, "language/lookup"),
    COUNTRY: createRoute(CONTROLLERS.CRM_CORE, "country/lookup"),
    REFERENCE_COUNTRIES: createRoute(CONTROLLERS.CRM_CORE, "ref/countries"),
    TEMPERATURE: createRoute(CONTROLLERS.CRM_CORE, "temperature/lookup"),
    CURRENCY: createRoute(CONTROLLERS.CRM_CORE, "currency/lookup"),
    WEIGHT: createRoute(CONTROLLERS.CRM_CORE, "weight/lookup"),
    VOLUME: createRoute(CONTROLLERS.CRM_CORE, "volume/lookup"),
    FUEL: createRoute(CONTROLLERS.CRM_CORE, "fuel/lookup"),
    DISTANCE: createRoute(CONTROLLERS.CRM_CORE, "distance/lookup"),
    POSITION: createRoute(CONTROLLERS.CRM_CORE, "position/lookup"),
    PRODUCT: createRoute(CONTROLLERS.CRM_CORE, "services/product-list/lookup"),
    CLIENT_SOURCE: createRoute(CONTROLLERS.CRM_CORE, "client-source/lookup"),
    CLIENT_COMPETITOR: createRoute(CONTROLLERS.CRM_CORE, "client-competitor/lookup"),
    CLIENT_POSITION_TYPE: createRoute(CONTROLLERS.CRM_CORE, "client-position-type/lookup"),
    PAYMENT_TYPE: createRoute(CONTROLLERS.CRM_CORE, "payment-type/lookup"),
    PAYMENT: createRoute(CONTROLLERS.CRM_CORE, "payment/lookup"),
    PAYMENT_SUBS_UNIT: createRoute(CONTROLLERS.CRM_CORE, "payment-subs-unit/lookup"),
    PPR_PAYMENT_TERM: createRoute(CONTROLLERS.CRM_CORE, "ppr-payment-term/lookup"),
    TIRE_MAKE: createRoute(CONTROLLERS.CRM_CORE, "tire-make/lookup"),
    UNIT_BRAND: createRoute(CONTROLLERS.CRM_CORE, "unit-make"),
    UNIT_MODELS: createRoute(CONTROLLERS.CRM_CORE, "unit-model"),
    UNIT_MODELS_BY_MAKE: createRoute(CONTROLLERS.CRM_CORE, "unit-model/{id}/lookup"),
    UNIT_TYPE: createRoute(CONTROLLERS.CRM_CORE, "unit-type/lookup"),
    PARAMETER_GROUP: createRoute(CONTROLLERS.CRM_CORE, "parameter-group/lookup"),
    PARAMETERS: createRoute(CONTROLLERS.CRM_CORE, "property"),
    PROPERTY_BY_MAKE_ID: createRoute(CONTROLLERS.CRM_CORE, "property?makeId={id}"),
    SYSTEM_TYPE: createRoute(CONTROLLERS.CRM_CORE, "system-type/list"),
    PROPERTIES_BY_MAKE: createRoute(CONTROLLERS.CRM_CORE, "property?makeId={id}"),
    VEHICLE_MAKE: createRoute(CONTROLLERS.CRM_CORE, "vehicle-make/lookup"),
  },
  TACHOGRAPH_CARDS: {
    LIST: createRoute(CONTROLLERS.TACHO, "company-card"),
    COLUMNS: createRoute(CONTROLLERS.CRM_CORE, "tacho-card/columns"),
    DETAIL: createRoute(CONTROLLERS.TACHO, "company-card"),
    CREATE: createRoute(CONTROLLERS.CRM_CORE, "tacho-card"),
    UPDATE: createRoute(CONTROLLERS.TACHO, "company-card"),
    DELETE: createRoute(CONTROLLERS.CRM_CORE, "tacho-card"),
  },
  TACHO_COMPANY: {
    LOOKUP: createRoute(CONTROLLERS.TACHO, "tacho-company/lookup"),
    SAVE_OR_UPDATE: createRoute(CONTROLLERS.TACHO, "tacho-company"),
    GET_BY_CUSTOMER: createRoute(CONTROLLERS.TACHO, "tacho-company/customer/{customerId}"),
  },
  TACHO_VEHICLE: {
    GET_BY_VEHICLE: createRoute(CONTROLLERS.TACHO, "tacho-vehicle/vehicle/{vehicleId}"),
    SAVE: createRoute(CONTROLLERS.TACHO, "tacho-vehicle/{vehicleId}"),
  },
  DRIVER_ACTIVITIES: {
    DRIVER_LOOKUP: createRoute("/ila/api/v1", "driver/lookup"),
    ACTIVITY: createRoute("/ila/api/v1", "tacho/driver-activity"),
  },
  RAW_DATA_LOG: {
    LIST: createRoute("/ila/api/v1", "raw-data"),
    DEVICE_LOOKUP: createRoute(CONTROLLERS.CRM_CORE, "device/lookup"),
  },
  CUSTOMER: {
    ADD: createRoute(CONTROLLERS.CRM_CLIENT, "client"),
    LISTING: createRoute(CONTROLLERS.CRM_CLIENT, "client"),
    GET: createRoute(CONTROLLERS.CRM_CLIENT, "client/{clientId}"),
    UPDATE: createRoute(CONTROLLERS.CRM_CLIENT, "client/{clientId}"),
    STATUS: createRoute(CONTROLLERS.CRM_CLIENT, "client/{clientId}/status"),
    DELETE: createRoute(CONTROLLERS.CRM_CLIENT, "client/{id}"),
    COUNT: createRoute(CONTROLLERS.CRM_CLIENT, "client/statistics/count"),
    DASHBOARD: createRoute(CONTROLLERS.CRM_CLIENT, "client/dashboard"),
    LIMITS: createRoute(CONTROLLERS.CRM_CLIENT, "clients/{clientId}/limits"),
    LIMIT_BY_CODE: createRoute(CONTROLLERS.CRM_CLIENT, "clients/{clientId}/limits/{code}"),
    RESET_LIMIT: createRoute(CONTROLLERS.CRM_CLIENT, "clients/{clientId}/limits/{code}/reset"),
  },
  VEHICLE: {
    LIST: createRoute("/ila/api/v1", "vehicle/list"),
    UPDATE: createRoute(CONTROLLERS.CRM_CORE, "vehicle/{id}"),
    MOVE_CUSTOMER: createRoute(CONTROLLERS.CRM_CORE, "vehicle/customer/move"),
  },
  TPMS: {
    TIRE_SCHEMA: createRoute(CONTROLLERS.ILA, "tpms/tire-schema"),
    VEHICLE: createRoute(CONTROLLERS.CRM_CORE, "vehicle"),
    TIRE: createRoute(CONTROLLERS.ILA, "tire"),
    TIRE_VEHICLE: createRoute(CONTROLLERS.ILA, "tire/vehicle"),
    TIRE_VEHICLE_SETTING: createRoute(CONTROLLERS.ILA, "tire-vehicle-setting"),
    TIRE_PRESET: createRoute(CONTROLLERS.CRM_CORE, "tr-preset"),
    TIRE_PRESET_LOOKUP: createRoute(CONTROLLERS.CRM_CORE, "tr-preset/lookup"),
    TIRE_MAKE_LOOKUP: createRoute(CONTROLLERS.CRM_CORE, "tire-make/lookup"),
    TIRE_MODEL_BY_MAKE_LOOKUP: createRoute(CONTROLLERS.CRM_CORE, "tire-model/lookup-by-brand"),
    TIRE_MEASURE_LOOKUP: createRoute(CONTROLLERS.CRM_CORE, "tire-measure/lookup"),
  },
  DEVICE_DETAILS: {
    GET: createRoute(CONTROLLERS.CRM_CORE, "device/device-details"),
    INFO: createRoute(CONTROLLERS.CRM_CORE, "device/device-info"),
    UPDATE: createRoute(CONTROLLERS.CRM_CORE, "device"),
    EMISSIONS: createRoute(CONTROLLERS.CRM_CORE, "ref/emissions"),
    POWER_UNITS: createRoute(CONTROLLERS.CRM_CORE, "ref/power-units"),
    NRMM_STAGES: createRoute(CONTROLLERS.CRM_CORE, "ref/nrmm-stages"),
    FUEL_TYPES: createRoute(CONTROLLERS.CRM_CORE, "fuel-type/lookup"),
    COUNTRIES: createRoute(CONTROLLERS.CRM_CORE, "ref/countries"),
    DRIVERS: createRoute("/ila/api/v1", "driver/lookup"),
  },
  CONNECTIVITY_HEATMAP: createRoute("/ila/api/v1", "crm/heatmap"),
  ROUTE_RAW_DATA: createRoute("/ila/api/v1", "route/raw"),
  FUEL_LEVEL_HISTORY: {
    HISTORY: createRoute("/ila/api/v1", "raw-data/io-data"),
    TANKS: createRoute("/ila/api/v1", "raw-data/tanks"),
  },
  FUEL_PROBES_CALIBRATION: {
    LIST: createRoute("/ila/api/v1", "fuel-prob-calibration/list"),
    IMPORT: createRoute("/ila/api/v1", "fuel-prob-calibration/import"),
    CREATE: createRoute("/ila/api/v1", "fuel-prob-calibration"),
    UPDATE: createRoute("/ila/api/v1", "fuel-prob-calibration/{id}"),
    DELETE: createRoute("/ila/api/v1", "fuel-prob-calibration/{id}"),
    EXPORT: createRoute("/ila/api/v1", "fuel-prob-calibration/export"),
  },
  DEVICE_INCOMING_LAST_VALUE: createRoute("/ila/api/v1", "incoming-values"),
  DEVICE_MAPPINGS: {
    LISTING: createRoute("/ila/api/v1", "mapping/unit"),
    ADD: createRoute("/ila/api/v1", "mapping"),
    UPDATE: createRoute("/ila/api/v1", "mapping"),
    DELETE: createRoute("/ila/api/v1", "mapping"),
  },
  MAPPING_PRESETS: {
    LISTING: createRoute("/ila/api/v1", "mapping-preset"),
    LISTING_BY_ID: createRoute("/ila/api/v1", "mapping-preset/{presetId}"),
    LISTING_BY_DEVICE_TYPE: createRoute("/ila/api/v1", "mapping-preset/deviceType"),
    PRESET_NAMES: createRoute("/ila/api/v1", "mapping-preset/preset-names"),
    CLONE_PRESET: createRoute("/ila/api/v1", "mapping-preset/clone"),
    USE_PRESET: createRoute("/ila/api/v1", "mapping/unit"),
    EXPORT_PRESET: createRoute("/ila/api/v1", "mapping/export-preset"),
    CREATE_MAPPING_PRESET: createRoute("/ila/api/v1", "mapping-preset/create-mapping"),
    UPDATE_MAPPING_PRESET: createRoute("/ila/api/v1", "mapping-preset"),
    DELETE_MAPPING_PRESET: createRoute("/ila/api/v1", "mapping-preset/delete-mapping"),
    DELETE: createRoute("/ila/api/v1", "mapping-preset/delete"),
    PROPERTY: createRoute("/ila/api/v1", "property"),
  },
  MAPPING_LOOKUP: {
    RELAY_STATUS_TYPE: createRoute(CONTROLLERS.CRM_CORE, "relay-status-type/lookup"),
    SWITCH_ON_OFF_STATE_TYPE: createRoute(CONTROLLERS.CRM_CORE, "switch-type/lookup"),
    TEMPERATURE_TYPE: createRoute("/crm-core/api/v1", "temperature-type/lookup"),
  },
  COMMAND_HISTORY: {
    LIST: createRoute(CONTROLLERS.COMMAND_SERVICE, "commands/units"),
  },
  AUDIT_LOG: {
    LIST: createRoute(CONTROLLERS.AUDIT_LOG, "audit-events"),
  },
  COMMANDS: {
    DETAIL: createRoute(CONTROLLERS.COMMAND_SERVICE, "commands"),
    UPDATE: createRoute(CONTROLLERS.CRM_CORE, "commands"),
    LOOKUP_BY_MODEL: createRoute(CONTROLLERS.COMMAND_SERVICE, "commands/models"),
    SEND: createRoute(CONTROLLERS.COMMAND_SERVICE, "commands/vehicles"),
    HISTORY_EVENTS: withBaseUrl(
      createRoute(CONTROLLERS.COMMAND_SERVICE, "commands/event/histories")
    ),
  },
  WAREHOUSE: {
    BASE: createRoute("/warehouse/api/v1", "warehouse"),
    LIST_BY_DISTRIBUTOR: createRoute("/warehouse/api/v1", "warehouse/list/distributor"),
    LOOKUP_BY_DISTRIBUTOR: createRoute("/warehouse/api/v1", "warehouse/list/dropdown/distributor"),
    TYPE_LOOKUP: createRoute(CONTROLLERS.CRM_CORE, "warehouse-type/lookup"),
  },
  WAREHOUSE_AREA: {
    BASE: createRoute("/warehouse/api/v1", "area"),
    LIST_BY_WAREHOUSE: createRoute("/warehouse/api/v1", "area/list/warehouse/{warehouseId}"),
    LOOKUP_BY_WAREHOUSE: createRoute(
      "/warehouse/api/v1",
      "area/list/dropdown/warehouse/{warehouseId}"
    ),
  },
  WAREHOUSE_LANE: {
    BASE: createRoute("/warehouse/api/v1", "lane"),
    LIST_BY_AREA: createRoute("/warehouse/api/v1", "lane/list/area/{areaId}"),
    LOOKUP_BY_AREA: createRoute("/warehouse/api/v1", "lane/list/dropdown/area/{areaId}"),
  },
  WAREHOUSE_SHELF: {
    BASE: createRoute("/warehouse/api/v1", "shelf"),
    LIST_BY_LANE: createRoute("/warehouse/api/v1", "shelf/list/lane/{laneId}"),
    LOOKUP_BY_LANE: createRoute("/warehouse/api/v1", "shelf/list/dropdown/lane/{laneId}"),
  },
  WAREHOUSE_POSITION: {
    BASE: createRoute("/warehouse/api/v1", "position"),
    LIST_BY_SHELF: createRoute("/warehouse/api/v1", "position/list/shelf/{shelfId}"),
    LOOKUP_BY_SHELF: createRoute("/warehouse/api/v1", "position/list/dropdown/shelf/{shelfId}"),
  },
  WAREHOUSE_UNIT: {
    LIST: createRoute("/warehouse/api/v1", "units/list"),
    SAVE: createRoute("/warehouse/api/v1", "units/save"),
    OPERATION_TYPE_LOOKUP: createRoute(CONTROLLERS.CRM_CORE, "unit-operation/list/lookup"),
    HW_STATUS_LOOKUP: createRoute(CONTROLLERS.CRM_CORE, "unit-status/lookup"),
  },
  UNIT_MANAGEMENT: {
    LOOKUP: {
      VEHICLE_MAKE: createRoute(CONTROLLERS.CRM_CORE, "vehicle-make/lookup"),
      VEHICLE_MODEL: createRoute(CONTROLLERS.CRM_CORE, "vehicle-model/lookup/{id}"),
      VEHICLE_TYPE: createRoute(CONTROLLERS.CRM_CORE, "vehicle-type/lookup"),
      MAX_SPEED: createRoute(CONTROLLERS.CRM_CORE, "speed/list"),
      ADJUSTER_LIST: createRoute(CONTROLLERS.CRM_CLIENT, "user/lookup/adjuster"),
      CLIENT_LIST: createRoute(CONTROLLERS.CRM_CLIENT, "client/lookup"),
      UNIT_SEARCH: createRoute(CONTROLLERS.WAREHOUSE, "units/search"),
      VEHICLE_SEARCH: createRoute(CONTROLLERS.CRM_CORE, "unit-management/uninstall/search"),
    },
    BASE: createRoute(CONTROLLERS.CRM_CORE, "unit-management"),
    UNINSTALL: createRoute(CONTROLLERS.CRM_CORE, "unit-management/unistall"),
    UNINSTALL_FUEL_SENSOR: createRoute(CONTROLLERS.CRM_CORE, "fuel-sensor/{id}"),
  },
  ADMINISTRATION: {
    LOOKUP: {
      UNIT_BRAND: createRoute(CONTROLLERS.CRM_CORE, "unit-make"),
      UNIT_MODELS: createRoute(CONTROLLERS.CRM_CORE, "unit-model"),
      UNIT_MODELS_BY_MAKE: createRoute(CONTROLLERS.CRM_CORE, "unit-model/{id}/lookup"),
      UNIT_MODEL_LOOKUP: createRoute(CONTROLLERS.CRM_CORE, "unit-model/lookup"),
      UNIT_TYPE: createRoute(CONTROLLERS.CRM_CORE, "unit-type/lookup"),
      PARAMETER_GROUP: createRoute(CONTROLLERS.CRM_CORE, "parameter-group/lookup"),
      PARAMETERS: createRoute(CONTROLLERS.CRM_CORE, "property"),
      PROPERTY_BY_MAKE_ID: createRoute(CONTROLLERS.CRM_CORE, "property?makeId={id}"),
      SYSTEM_TYPE: createRoute(CONTROLLERS.CRM_CORE, "system-type/list"),
      PROPERTIES_BY_MAKE: createRoute(CONTROLLERS.CRM_CORE, "property?makeId={id}"),
      VEHICLE_MAKE: createRoute(CONTROLLERS.CRM_CORE, "vehicle-make/lookup"),
    },
    SETUP: {
      UNIT_BRAND: {
        ADD: createRoute(CONTROLLERS.CRM_CORE, "unit-make"),
        GET: createRoute(CONTROLLERS.CRM_CORE, "unit-make/{id}"),
        UPDATE: createRoute(CONTROLLERS.CRM_CORE, "unit-make/{id}"),
        DELETE: createRoute(CONTROLLERS.CRM_CORE, "unit-make/{id}"),
        LISTING: createRoute(CONTROLLERS.CRM_CORE, "unit-make/list"),
        COLUMN: createRoute(CONTROLLERS.CRM_CORE, "unit-make/columns"),
      },
      UNIT_MODELS: {
        ADD: createRoute(CONTROLLERS.CRM_CORE, "unit-model"),
        GET: createRoute(CONTROLLERS.CRM_CORE, "unit-model/{id}"),
        UPDATE: createRoute(CONTROLLERS.CRM_CORE, "unit-model/{id}"),
        DELETE: createRoute(CONTROLLERS.CRM_CORE, "unit-model/{id}"),
        LISTING: createRoute(CONTROLLERS.CRM_CORE, "unit-model/list"),
        COLUMN: createRoute(CONTROLLERS.CRM_CORE, "unit-model/columns"),
      },
      TEMPERATURE_SENSOR_TYPES: {
        ADD: createRoute(CONTROLLERS.CRM_CORE, "temperature-type"),
        GET: createRoute(CONTROLLERS.CRM_CORE, "temperature-type/{id}"),
        UPDATE: createRoute(CONTROLLERS.CRM_CORE, "temperature-type/{id}"),
        DELETE: createRoute(CONTROLLERS.CRM_CORE, "temperature-type/{id}"),
        LISTING: createRoute(CONTROLLERS.CRM_CORE, "temperature-type/list"),
      },
      SWITCH_ON_OFF_STATES: {
        ADD: createRoute(CONTROLLERS.CRM_CORE, "switch-type"),
        GET: createRoute(CONTROLLERS.CRM_CORE, "switch-type/{id}"),
        UPDATE: createRoute(CONTROLLERS.CRM_CORE, "switch-type/{id}"),
        DELETE: createRoute(CONTROLLERS.CRM_CORE, "switch-type/{id}"),
        LISTING: createRoute(CONTROLLERS.CRM_CORE, "switch-type/list"),
      },
      RELAY_STATUS_TYPES: {
        ADD: createRoute(CONTROLLERS.CRM_CORE, "relay-status-type"),
        GET: createRoute(CONTROLLERS.CRM_CORE, "relay-status-type/{id}"),
        UPDATE: createRoute(CONTROLLERS.CRM_CORE, "relay-status-type/{id}"),
        DELETE: createRoute(CONTROLLERS.CRM_CORE, "relay-status-type/{id}"),
        LISTING: createRoute(CONTROLLERS.CRM_CORE, "relay-status-type/list"),
      },
      VEHICLE_TYPE: {
        ADD: createRoute(CONTROLLERS.CRM_CORE, "vehicle-type"),
        GET: createRoute(CONTROLLERS.CRM_CORE, "vehicle-type/{id}"),
        UPDATE: createRoute(CONTROLLERS.CRM_CORE, "vehicle-type/{id}"),
        DELETE: createRoute(CONTROLLERS.CRM_CORE, "vehicle-type/{id}"),
        LISTING: createRoute(CONTROLLERS.CRM_CORE, "vehicle-type/list"),
        COLUMN: createRoute(CONTROLLERS.CRM_CORE, "vehicle-type/columns"),
      },
      VEHICLE_MAKE: {
        ADD: createRoute(CONTROLLERS.CRM_CORE, "vehicle-make"),
        GET: createRoute(CONTROLLERS.CRM_CORE, "vehicle-make/{id}"),
        UPDATE: createRoute(CONTROLLERS.CRM_CORE, "vehicle-make/{id}"),
        DELETE: createRoute(CONTROLLERS.CRM_CORE, "vehicle-make/{id}"),
        LISTING: createRoute(CONTROLLERS.CRM_CORE, "vehicle-make/list"),
        COLUMN: createRoute(CONTROLLERS.CRM_CORE, "vehicle-make/columns"),
      },
      VEHICLE_MODEL: {
        ADD: createRoute(CONTROLLERS.CRM_CORE, "vehicle-model"),
        GET: createRoute(CONTROLLERS.CRM_CORE, "vehicle-model/{id}"),
        UPDATE: createRoute(CONTROLLERS.CRM_CORE, "vehicle-model/{id}"),
        DELETE: createRoute(CONTROLLERS.CRM_CORE, "vehicle-model/{id}"),
        LISTING: createRoute(CONTROLLERS.CRM_CORE, "vehicle-model/list"),
        COLUMN: createRoute(CONTROLLERS.CRM_CORE, "vehicle-model/columns"),
      },
      TIRE_MAKES: {
        ADD: createRoute(CONTROLLERS.CRM_CORE, "tire-make"),
        GET: createRoute(CONTROLLERS.CRM_CORE, "tire-make/{id}"),
        UPDATE: createRoute(CONTROLLERS.CRM_CORE, "tire-make/{id}"),
        DELETE: createRoute(CONTROLLERS.CRM_CORE, "tire-make/{id}"),
        LISTING: createRoute(CONTROLLERS.CRM_CORE, "tire-make"),
      },
      TIRE_MODELS: {
        ADD: createRoute(CONTROLLERS.CRM_CORE, "tire-model"),
        GET: createRoute(CONTROLLERS.CRM_CORE, "tire-model/{id}"),
        UPDATE: createRoute(CONTROLLERS.CRM_CORE, "tire-model/{id}"),
        DELETE: createRoute(CONTROLLERS.CRM_CORE, "tire-model/{id}"),
        LISTING: createRoute(CONTROLLERS.CRM_CORE, "tire-model"),
      },
      TIRE_MEASURE: {
        ADD: createRoute(CONTROLLERS.CRM_CORE, "tire-measure"),
        GET: createRoute(CONTROLLERS.CRM_CORE, "tire-measure/{id}"),
        UPDATE: createRoute(CONTROLLERS.CRM_CORE, "tire-measure/{id}"),
        DELETE: createRoute(CONTROLLERS.CRM_CORE, "tire-measure/{id}"),
        LISTING: createRoute(CONTROLLERS.CRM_CORE, "tire-measure"),
      },
      TIRE_PRESET: {
        ADD: createRoute(CONTROLLERS.CRM_CORE, "tr-preset"),
        GET: createRoute(CONTROLLERS.CRM_CORE, "tr-preset/{id}"),
        UPDATE: createRoute(CONTROLLERS.CRM_CORE, "tr-preset/{id}"),
        DELETE: createRoute(CONTROLLERS.CRM_CORE, "tr-preset/{id}"),
        LISTING: createRoute(CONTROLLERS.CRM_CORE, "tr-preset"),
      },
      PARAMETER_GROUP: {
        ADD: createRoute(CONTROLLERS.CRM_CORE, "parameter-group"),
        GET: createRoute(CONTROLLERS.CRM_CORE, "parameter-group/{id}"),
        UPDATE: createRoute(CONTROLLERS.CRM_CORE, "parameter-group/{id}"),
        DELETE: createRoute(CONTROLLERS.CRM_CORE, "parameter-group/{id}"),
        LISTING: createRoute(CONTROLLERS.CRM_CORE, "parameter-group/list"),
        COLUMN: createRoute(CONTROLLERS.CRM_CORE, "parameter-group/columns"),
      },
      PARAMETERS: {
        ADD: createRoute(CONTROLLERS.CRM_CORE, "property"),
        GET: createRoute(CONTROLLERS.CRM_CORE, "property/{id}"),
        UPDATE: createRoute(CONTROLLERS.CRM_CORE, "property/{id}"),
        DELETE: createRoute(CONTROLLERS.CRM_CORE, "property/{id}"),
        LISTING: createRoute(CONTROLLERS.CRM_CORE, "property/list"),
        COLUMN: createRoute(CONTROLLERS.CRM_CORE, "property/columns"),
      },
      CUSTOMER_TYPE: {
        ADD: createRoute(CONTROLLERS.CRM_CORE, "client-type"),
        UPDATE: createRoute(CONTROLLERS.CRM_CORE, "client-type/{id}"),
        DELETE: createRoute(CONTROLLERS.CRM_CORE, "client-type/{id}"),
        LISTING: createRoute(CONTROLLERS.CRM_CORE, "client-type/list"),
        COLUMN: createRoute(CONTROLLERS.CRM_CORE, "client-type/columns"),
      },
      CUSTOMER_SOURCE: {
        ADD: createRoute(CONTROLLERS.CRM_CORE, "client-source"),
        UPDATE: createRoute(CONTROLLERS.CRM_CORE, "client-source/{id}"),
        DELETE: createRoute(CONTROLLERS.CRM_CORE, "client-source/{id}"),
        LISTING: createRoute(CONTROLLERS.CRM_CORE, "client-source"),
        COLUMN: createRoute(CONTROLLERS.CRM_CORE, "client-source/columns"),
      },
      CORPORATION_POSITION: {
        ADD: createRoute(CONTROLLERS.CRM_CORE, "position"),
        UPDATE: createRoute(CONTROLLERS.CRM_CORE, "position/{id}"),
        DELETE: createRoute(CONTROLLERS.CRM_CORE, "position/{id}"),
        LISTING: createRoute(CONTROLLERS.CRM_CORE, "position"),
        COLUMN: createRoute(CONTROLLERS.CRM_CORE, "position/columns"),
      },
      BUSINESS_ACTIVITIES: {
        ADD: createRoute(CONTROLLERS.CRM_CORE, "client-business-type"),
        UPDATE: createRoute(CONTROLLERS.CRM_CORE, "client-business-type/{id}"),
        DELETE: createRoute(CONTROLLERS.CRM_CORE, "client-business-type/{id}"),
        LISTING: createRoute(CONTROLLERS.CRM_CORE, "client-business-type/list"),
        COLUMN: createRoute(CONTROLLERS.CRM_CORE, "client-business-type/columns"),
      },
      ADD_MODEL_PROPERTIES: {
        ADD: createRoute(CONTROLLERS.CRM_CORE, "model-property"),
        GET: createRoute(CONTROLLERS.CRM_CORE, "model-property/{id}"),
        UPDATE: createRoute(CONTROLLERS.CRM_CORE, "model-property/{id}"),
        DELETE: createRoute(CONTROLLERS.CRM_CORE, "model-property/{id}"),
        LISTING: createRoute(CONTROLLERS.CRM_CORE, "model-property/list"),
        COLUMN: createRoute(CONTROLLERS.CRM_CORE, "model-property/columns"),
      },
    },
    UNIT_MANAGEMENT: {
      LOOKUP: {
        VEHICLE_MAKE: createRoute(CONTROLLERS.CRM_CORE, "vehicle-make/lookup"),
        VEHICLE_MODEL: createRoute(CONTROLLERS.CRM_CORE, "vehicle-model/lookup/{id}"),
        VEHICLE_TYPE: createRoute(CONTROLLERS.CRM_CORE, "vehicle-type/lookup"),
        MAX_SPEED: createRoute(CONTROLLERS.CRM_CORE, "speed/list"),
        ADJUSTER_LIST: createRoute(CONTROLLERS.CRM_CLIENT, "user/lookup/adjuster"),
        CLIENT_LIST: createRoute(CONTROLLERS.CRM_CLIENT, "client/lookup"),
        UNIT_SEARCH: createRoute(CONTROLLERS.WAREHOUSE, "units/search"),
        VEHICLE_SEARCH: createRoute(CONTROLLERS.CRM_CORE, "unit-management/uninstall/search"),
      },
      BASE: createRoute(CONTROLLERS.CRM_CORE, "unit-management"),
      UNINSTALL: createRoute(CONTROLLERS.CRM_CORE, "unit-management/unistall"),
      UNINSTALL_FUEL_SENSOR: createRoute(CONTROLLERS.CRM_CORE, "fuel-sensor/{id}"),
    },
  },
} as const;
